import { FormEvent, useEffect, useMemo, useState } from 'react';
import { signalHintSourceLabel } from '../lib/decode/signalHints';

type DecodeContribution = {
  id: string;
  createdAt: string;
  canId: number;
  canIdHex: string;
  signalName: string;
  description: string;
  dataPattern: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
  evidenceNote: string;
  status: 'pending' | 'verified' | 'rejected' | string;
  promotedSignal?: DecodeSignal | null;
};

type StatusFilter = 'all' | 'verified' | 'pending';
type DecodeTab = 'signals' | 'contributions';

type DecodeSignal = {
  id: string;
  createdAt: string;
  sourceContributionId?: string | null;
  canId: number;
  canIdHex: string;
  signalName: string;
  description: string;
  dataPattern: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
  vehicleBrand?: string | null;
  vehicleHint?: string | null;
  messageName?: string | null;
  startBit?: number | null;
  length?: number | null;
  endianness?: string | null;
  factor?: number | null;
  offset?: number | null;
  unit?: string | null;
  category?: string | null;
  usableForAutoMatch?: boolean;
  needsRealValidation?: boolean;
  status: 'active' | string;
  source: 'community_verified' | string;
  confidence: number;
  confidenceLabel?: string | null;
};

function formatTimestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString();
}

function vehicleLabel(contribution: DecodeContribution) {
  return [contribution.vehicleYear, contribution.vehicleMake, contribution.vehicleModel]
    .filter(Boolean)
    .join(' ');
}

function signalVehicleLabel(signal: DecodeSignal) {
  return [signal.vehicleYear, signal.vehicleMake ?? signal.vehicleBrand, signal.vehicleModel ?? signal.vehicleHint]
    .filter(Boolean)
    .join(' ');
}

function shortText(value: string, maxLength = 180) {
  if (!value) return 'No description provided yet.';
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}...` : value;
}

function signalStatusLabel(signal: DecodeSignal) {
  if (signal.source === 'community_verified') return 'verified signal';
  if (signal.source === 'opendbc_seed') return 'opendbc seed';
  return 'community data';
}

function signalStatusClass(signal: DecodeSignal) {
  if (signal.source === 'community_verified') return 'status-verified';
  if (signal.source === 'opendbc_seed') return 'status-pending';
  return 'status-pending';
}

export function DecodePage() {
  const [contributions, setContributions] = useState<DecodeContribution[]>([]);
  const [signals, setSignals] = useState<DecodeSignal[]>([]);
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [signalsState, setSignalsState] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const [signalsError, setSignalsError] = useState('');
  const [warning, setWarning] = useState('');
  const [signalsWarning, setSignalsWarning] = useState('');
  const [canIdFilter, setCanIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [activeTab, setActiveTab] = useState<DecodeTab>('signals');
  const [adminSecret, setAdminSecret] = useState('');
  const [reviewState, setReviewState] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [reviewMessage, setReviewMessage] = useState('');
  const isAdminMode = useMemo(() => new URLSearchParams(window.location.search).get('admin') === 'true', []);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (canIdFilter.trim()) params.set('can_id', canIdFilter.trim());
    return `/api/decode/contributions?${params.toString()}`;
  }, [canIdFilter]);

  const signalsEndpoint = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (canIdFilter.trim()) params.set('can_id', canIdFilter.trim());
    return `/api/decode/signals?${params.toString()}`;
  }, [canIdFilter]);

  async function loadContributions(url = endpoint) {
    setState('loading');
    setError('');
    setWarning('');

    try {
      const response = await fetch(url);
      const payload = await response.json() as { ok?: boolean; contributions?: DecodeContribution[]; error?: string; warning?: string };
      if (!response.ok || !payload.ok) throw new Error(payload.error || 'Unable to load contributions.');
      setContributions(Array.isArray(payload.contributions) ? payload.contributions : []);
      setWarning(payload.warning ?? '');
      setState('success');
    } catch (loadError) {
      setContributions([]);
      setState('error');
      setError(loadError instanceof Error ? loadError.message : 'Unable to load contributions.');
    }
  }

  async function loadSignals(url = signalsEndpoint) {
    setSignalsState('loading');
    setSignalsError('');
    setSignalsWarning('');

    try {
      const response = await fetch(url);
      const payload = await response.json() as { ok?: boolean; signals?: DecodeSignal[]; error?: string; warning?: string };
      if (!response.ok || !payload.ok) throw new Error(payload.error || 'Unable to load catalog signals.');
      setSignals(Array.isArray(payload.signals) ? payload.signals : []);
      setSignalsWarning(payload.warning ?? '');
      setSignalsState('success');
    } catch (loadError) {
      setSignals([]);
      setSignalsState('error');
      setSignalsError(loadError instanceof Error ? loadError.message : 'Unable to load catalog signals.');
    }
  }

  useEffect(() => {
    void loadContributions(endpoint);
    void loadSignals(signalsEndpoint);
  }, [endpoint, signalsEndpoint]);

  function applyFilter(event: FormEvent) {
    event.preventDefault();
    void loadContributions(endpoint);
    void loadSignals(signalsEndpoint);
  }

  const visibleContributions = useMemo(() => {
    if (statusFilter === 'all') return contributions;
    return contributions.filter((contribution) => contribution.status === statusFilter);
  }, [contributions, statusFilter]);

  async function reviewContribution(id: string, action: 'verify' | 'reject') {
    setReviewState('loading');
    setReviewMessage('');

    try {
      const response = await fetch('/api/decode/review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': adminSecret
        },
        body: JSON.stringify({ id, action })
      });
      const payload = await response.json() as { ok?: boolean; error?: string; contribution?: DecodeContribution };
      if (!response.ok || !payload.ok || !payload.contribution) throw new Error(payload.error || 'Review update failed.');
      const reviewedContribution = payload.contribution;
      const promotedSignal = reviewedContribution.promotedSignal;
      setContributions((current) => current.map((item) => item.id === reviewedContribution.id ? reviewedContribution : item));
      if (promotedSignal) {
        setSignals((current) => {
          const withoutDuplicate = current.filter((signal) => signal.id !== promotedSignal.id);
          return [promotedSignal, ...withoutDuplicate].slice(0, 50);
        });
      }
      setReviewState('success');
      setReviewMessage(`Contribution ${action === 'verify' ? 'verified' : 'rejected'}.`);
    } catch (reviewError) {
      setReviewState('error');
      setReviewMessage(reviewError instanceof Error ? reviewError.message : 'Review update failed.');
    }
  }

  return (
    <main className="decode-page">
      <section className="section page-shell decode-hero">
        <div className="section-heading compact">
          <p className="eyebrow">SmartCAN community dataset</p>
          <h1>Community Decode Contributions</h1>
          <p className="lead">Browse user-submitted CAN signal suggestions and evidence. Contributions are pending review before they become verified decode definitions.</p>
          <p>Verified community signals, opendbc seed signals, and medium-confidence public dataset records are used as hints in Analyzer and Live Mode. Numeric decoding requires formula and bit definitions and will come in a later phase.</p>
        </div>
        <form className="decode-filter-card" onSubmit={applyFilter}>
          <label>
            Filter by CAN ID
            <input value={canIdFilter} onChange={(event) => setCanIdFilter(event.target.value)} placeholder="0x025" />
          </label>
          <label>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <div className="control-row">
            <button className="button secondary" type="submit">Apply</button>
            <button className="button ghost" type="button" disabled={!canIdFilter} onClick={() => setCanIdFilter('')}>Clear</button>
          </div>
        </form>
      </section>

      <section className="section decode-list-section">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Decode catalog</p>
            <h2>{activeTab === 'signals' ? 'Catalog Signals' : 'Community submissions'}</h2>
            <p className="panel-note">
              {activeTab === 'signals'
                ? 'SmartCAN verified signals and opendbc seed records are reusable as decode hints in Analyzer and Live Mode.'
                : 'Signal suggestions submitted by users. Pending entries become verified after review.'}
            </p>
          </div>
          <span className={`parse-state ${activeTab === 'signals' ? signalsState : state}`}>
            State: {activeTab === 'signals' ? signalsState : state}
          </span>
        </div>
        <div className="decode-tabs" role="tablist" aria-label="Decode dataset sections">
          <button className={activeTab === 'signals' ? 'active' : ''} type="button" role="tab" aria-selected={activeTab === 'signals'} onClick={() => setActiveTab('signals')}>
            Catalog Signals
          </button>
          <button className={activeTab === 'contributions' ? 'active' : ''} type="button" role="tab" aria-selected={activeTab === 'contributions'} onClick={() => setActiveTab('contributions')}>
            Contributions
          </button>
        </div>
        {isAdminMode && (
          <div className="admin-review-panel">
            <label>
              Admin secret
              <input type="password" value={adminSecret} onChange={(event) => setAdminSecret(event.target.value)} placeholder="Admin review key" />
            </label>
            <p className="panel-note">Admin controls are hidden unless the page URL includes <code>?admin=true</code>.</p>
            {reviewMessage && <p className={reviewState === 'error' ? 'error-text' : 'success-text'}>{reviewMessage}</p>}
          </div>
        )}
        {activeTab === 'signals' && signalsError && <p className="error-text">{signalsError}</p>}
        {activeTab === 'contributions' && error && <p className="error-text">{error}</p>}
        {(warning === 'database not configured' || signalsWarning === 'database not configured') && (
          <div className="readiness-banner">
            <strong>Community dataset is not yet active in this environment.</strong>
            <span>Decode submissions will appear here after the production database connection is configured.</span>
          </div>
        )}
        {activeTab === 'signals' ? (
          <div className="contribution-list signal-list">
            {signalsState === 'loading' ? (
              <article className="decode-contribution-card"><p>Loading catalog signals...</p></article>
            ) : signals.length === 0 ? (
              <article className="decode-contribution-card">
                <h3>No catalog signals yet</h3>
                <p className="panel-note">
                  {signalsWarning === 'database not configured'
                    ? 'The page is ready, but persistence is waiting on the database environment variable.'
                    : 'Verified community signals and seed records will appear here after setup.'}
                </p>
              </article>
            ) : signals.map((signal) => (
              <article className="decode-contribution-card verified-signal-card" key={signal.id}>
                <div className="contribution-card-heading">
                  <div>
                    <p className="eyebrow">{signal.canIdHex}</p>
                    <h3>{signal.signalName}</h3>
                  </div>
                  <span className={`status-pill ${signalStatusClass(signal)}`}>
                    {signalStatusLabel(signal)}
                  </span>
                </div>
                <p>{shortText(signal.description)}</p>
                {signal.dataPattern && <p className="panel-note">Pattern: {signal.dataPattern}</p>}
                {signalVehicleLabel(signal) && <p className="panel-note">Vehicle/context: {signalVehicleLabel(signal)}</p>}
                {signal.messageName && <p className="panel-note">Message: {signal.messageName}</p>}
                <div className="signal-meta-row">
                  <span>Source: {signalHintSourceLabel(signal)}</span>
                  <span>Confidence: {signal.confidenceLabel ?? `${Math.round(signal.confidence * 100)}%`}</span>
                  <span>{formatTimestamp(signal.createdAt)}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="contribution-list">
            {state === 'loading' ? (
              <article className="decode-contribution-card"><p>Loading contributions...</p></article>
          ) : visibleContributions.length === 0 ? (
            <article className="decode-contribution-card">
              <h3>No contributions yet</h3>
              <p className="panel-note">
                {warning === 'database not configured'
                  ? 'The page is ready, but persistence is waiting on the database environment variable.'
                  : 'Submit a signal suggestion from the Analyzer or Live page to start the community dataset.'}
              </p>
            </article>
          ) : visibleContributions.map((contribution) => (
            <article className="decode-contribution-card" key={contribution.id}>
              <div className="contribution-card-heading">
                <div>
                  <p className="eyebrow">{contribution.canIdHex}</p>
                  <h3>{contribution.signalName}</h3>
                </div>
                <span className={`status-pill status-${contribution.status}`}>{contribution.status}</span>
              </div>
              <p>{shortText(contribution.description)}</p>
              {contribution.dataPattern && <p className="panel-note">Pattern: {contribution.dataPattern}</p>}
              {vehicleLabel(contribution) && <p className="panel-note">Vehicle/context: {vehicleLabel(contribution)}</p>}
              <small>{formatTimestamp(contribution.createdAt)}</small>
              {isAdminMode && (
                <div className="admin-review-actions">
                  <button className="button secondary small" type="button" disabled={!adminSecret || contribution.status === 'verified' || reviewState === 'loading'} onClick={() => void reviewContribution(contribution.id, 'verify')}>Verify</button>
                  <button className="button ghost small danger-action" type="button" disabled={!adminSecret || contribution.status === 'rejected' || reviewState === 'loading'} onClick={() => void reviewContribution(contribution.id, 'reject')}>Reject</button>
                </div>
              )}
            </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
